import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getUploadsUrl } from '../services/api';
import { borneType } from '../utils/borneLabel';
import './SearchSelect.css';
import './BornePicker.css';

/**
 * Choix d'une borne, regroupée sous le contact qui l'a :
 *
 *   [photo] Prénom Nom – Ville
 *       C123 – Classik V3
 *   Bornes non assignées
 *       C298 ⚠ n° estimé – Classik V3
 *
 * Quand la recherche correspond à une ville, les contacts sont en plus
 * rangés sous leur ville. La recherche porte sur le numéro, le n° de série,
 * la gamme, le modèle, le contact et sa ville ; plusieurs mots, dans
 * n'importe quel ordre, sans tenir compte des accents.
 *
 * Props : bornes (lignes de /prets-bornes/options), value (id en texte),
 * onChange(id en texte), preferredContactId (ses bornes passent en tête).
 */

const normalize = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// Numéro affiché. Quelques bornes n'ont pas de numéro complet dans l'app
// bornes : on le recompose avec l'initiale de la gamme, et on le signale.
export const borneNumero = (b) => {
  if (b.numero_formated) return b.numero_formated;
  if (b.numero) return `${(b.gamme_nom || '').charAt(0).toUpperCase()}${b.numero}`;
  return `Borne #${b.id}`;
};

const holderName = (b) => [b.contact_prenom, b.contact_nom].map(s => (s || '').trim()).filter(Boolean).join(' ');
const hasHolder = (b) => !!b.contact_antenne_id && holderName(b) !== '';
const byNumero = (a, b) => borneNumero(a).localeCompare(borneNumero(b), 'fr', { numeric: true });

const avatarOf = (b) => (b.contact_photo
  ? getUploadsUrl(b.contact_photo)
  : (b.contact_civilite === 'm' ? '/img/avatar-homme.svg' : '/img/avatar-femme.svg'));

// Regroupe les bornes trouvées : contacts (et villes si la recherche en
// désigne une), puis bornes non assignées.
export function groupBornes(bornes, query, preferredContactId) {
  const words = normalize(query).split(/\s+/).filter(Boolean);
  const matches = bornes.filter(b => {
    if (words.length === 0) return true;
    const hay = normalize([
      borneNumero(b), b.numero, b.numero_serie, b.gamme_nom, b.model_nom,
      b.contact_prenom, b.contact_nom, b.contact_ville,
    ].join(' '));
    return words.every(w => hay.includes(w));
  });

  // Contacts (avec leurs bornes), bornes non assignées, et à part les bornes
  // sans numéro complet (affichées avec leur numéro recomposé).
  const contacts = new Map();
  const unassigned = [];
  const incomplete = [];
  for (const b of matches) {
    if (!b.numero_formated) { incomplete.push(b); continue; }
    if (!hasHolder(b)) { unassigned.push(b); continue; }
    if (!contacts.has(b.contact_antenne_id)) {
      contacts.set(b.contact_antenne_id, { id: b.contact_antenne_id, name: holderName(b), ville: b.contact_ville || '', avatar: avatarOf(b), bornes: [] });
    }
    contacts.get(b.contact_antenne_id).bornes.push(b);
  }
  const pref = preferredContactId ? Number(preferredContactId) : null;
  const contactList = [...contacts.values()]
    .map(c => ({ ...c, bornes: c.bornes.sort(byNumero) }))
    .sort((a, b) => (a.id === pref ? -1 : b.id === pref ? 1 : a.name.localeCompare(b.name, 'fr')));
  unassigned.sort(byNumero);
  incomplete.sort(byNumero);

  // Mode ville : la recherche correspond au nom d'une ville de contact.
  const villeMode = words.length > 0 && contactList.some(c => {
    const v = normalize(c.ville);
    return v && words.every(w => v.includes(w));
  });
  let villes = [];
  if (villeMode) {
    const byVille = new Map();
    for (const c of contactList) {
      const key = c.ville || 'Sans ville';
      if (!byVille.has(key)) byVille.set(key, []);
      byVille.get(key).push(c);
    }
    villes = [...byVille.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], 'fr'))
      .map(([ville, list]) => ({ ville, contacts: list }));
  }
  return { villeMode, villes, contacts: contactList, unassigned, incomplete, count: matches.length };
}

export default function BornePicker({ bornes, value, onChange, preferredContactId, placeholder = 'Rechercher une borne' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => { if (open && inputRef.current) inputRef.current.focus(); }, [open]);

  const selected = useMemo(() => bornes.find(b => String(b.id) === String(value)), [bornes, value]);

  const result = useMemo(() => groupBornes(bornes, query, preferredContactId), [bornes, query, preferredContactId]);

  const pick = (b) => {
    onChange(b ? String(b.id) : '');
    setOpen(false);
    setQuery('');
  };

  const renderBorne = (b, mine, showHolder = false) => (
    <div
      key={b.id}
      className={`bp-borne${String(b.id) === String(value) ? ' selected' : ''}`}
      onClick={() => pick(b)}
    >
      <span className="bp-borne-num">{mine ? '★ ' : ''}{borneNumero(b)}</span>
      {!b.numero_formated && <span className="bp-estime" title="Numéro recomposé : il manque dans l'app bornes">⚠ n° estimé</span>}
      <span className="bp-borne-type">{borneType(b)}</span>
      {showHolder && hasHolder(b) && <span className="bp-contact-ville">· {holderName(b)}</span>}
      {b.numero_serie && <span className="bp-borne-serie">{b.numero_serie}</span>}
    </div>
  );

  const renderContact = (c) => (
    <div key={c.id} className="bp-contact">
      <div className="bp-contact-head">
        <img src={c.avatar} alt="" onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
        <span className="bp-contact-name">{c.name}</span>
        {c.ville && <span className="bp-contact-ville">– {c.ville}</span>}
      </div>
      {c.bornes.map(b => renderBorne(b, preferredContactId && Number(preferredContactId) === c.id))}
    </div>
  );

  return (
    <div className="search-select borne-picker" ref={ref}>
      <div className={`search-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
        {selected ? (
          <span className="search-select-value">
            <span>
              {borneNumero(selected)}{!selected.numero_formated ? ' ⚠' : ''} – {borneType(selected)}
              {hasHolder(selected) ? ` · ${holderName(selected)}` : ''}
            </span>
            <button type="button" className="search-select-clear" onClick={(e) => { e.stopPropagation(); pick(null); }}>&times;</button>
          </span>
        ) : (
          <span className="search-select-placeholder">{placeholder}</span>
        )}
        <svg className="search-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="search-select-dropdown bp-dropdown">
          <div className="search-select-input-wrapper">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Numéro, contact, ville, gamme, modèle, n° de série…"
              className="search-select-input"
            />
          </div>
          <div className="bp-results">
            {result.count === 0 && <div className="search-select-empty">Aucune borne</div>}
            {result.villeMode
              ? result.villes.map(v => (
                <div key={v.ville} className="bp-ville">
                  <div className="bp-ville-head">{v.ville}</div>
                  {v.contacts.map(renderContact)}
                </div>
              ))
              : result.contacts.map(renderContact)}
            {result.unassigned.length > 0 && (
              <div className="bp-contact">
                <div className="bp-contact-head bp-unassigned">Bornes non assignées</div>
                {result.unassigned.map(b => renderBorne(b, false))}
              </div>
            )}
            {result.incomplete.length > 0 && (
              <div className="bp-contact">
                <div className="bp-contact-head bp-unassigned" title="Numéro complet absent de l'app bornes : numéro recomposé avec la gamme">
                  Bornes sans nom complet
                </div>
                {result.incomplete.map(b => renderBorne(b, false, true))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
